import React, { useState, useEffect, ReactNode, useContext } from 'react'
import i18n from 'live/services/i18n'
import Select from 'live/components/forms/ui/select'
import { timestampToDateTime, dateTimeToTimestamp, dateAndTimeRegex } from '../../utils/parse'
import { RawInterventionEvent, SelectOptions } from '../../utils/type'
import { CustomDatePicker, ModalField, ModalSectionHeader } from '../modal-components'
import MaskedInput from 'react-maskedinput'
import { STATIC_DATA_STATES } from '../chart-components'
import { InterventionErrors } from './types'
import { fetchEntityOptions, INTERVENTION_API } from '../../api/api'
import { join } from 'lodash'
import { StaticDataContext } from '../../context/StaticDataContext'

interface InterventionOptionsTemplate {
    tatic: SelectOptions[]
}

interface InterventionTabProps {
    event: RawInterventionEvent
    children?: ReactNode
    errors: InterventionErrors
    updateInterventionEvent: (event: RawInterventionEvent) => void
    hasPermissionToEdit: boolean
}

const InterventionTab: React.FC<InterventionTabProps> = ({
    event,
    errors,
    updateInterventionEvent,
    hasPermissionToEdit
}: InterventionTabProps) => {
    const eventStartDateTime = event.start ? timestampToDateTime(event.start) : null
    const eventEndDateTime = event.end ? timestampToDateTime(event.end) : null
    const [selectedEvent, setSelectedEvent] = useState<RawInterventionEvent>(event)
    const [taticList, setTaticList] = useState<SelectOptions[]>([])
    const [startDate, setStartDate] = useState<string | null>(eventStartDateTime?.date || null)
    const [startHour, setStartHour] = useState<string | null>(eventStartDateTime?.time || null)
    const [endDate, setEndDate] = useState<string | null>(eventEndDateTime?.date || null)
    const [endHour, setEndHour] = useState<string | null>(eventEndDateTime?.time || null)
    const { liveTheme } = useContext(StaticDataContext)

    const fieldDisabledAfterEntityStart = selectedEvent.state !== STATIC_DATA_STATES.EMPTY

    useEffect(() => {
        fetchEntityOptions(INTERVENTION_API).then((e: InterventionOptionsTemplate | null) => {
            if (!e) return
            setTaticList(e.tatic as SelectOptions[])
        })
    }, [])

    useEffect(() => {
        if (dateAndTimeRegex.test(join([startDate, startHour], ' '))) {
            const timestamp = dateTimeToTimestamp(join([startDate, startHour], ' '))
            setSelectedEvent(prevstate => ({ ...prevstate, start: timestamp }))
        }
    }, [startDate, startHour])

    useEffect(() => {
        if (dateAndTimeRegex.test(join([endDate, endHour], ' '))) {
            const timestamp = dateTimeToTimestamp(join([endDate, endHour], ' '))
            setSelectedEvent(prevstate => ({ ...prevstate, end: timestamp }))
        }
    }, [endDate, endHour])

    // required field
    useEffect(() => {
        if (startHour === '') {
            setSelectedEvent(prevstate => ({ ...prevstate, start: 0 }))
        }
        if (endHour === '') {
            setSelectedEvent(prevstate => ({ ...prevstate, end: 0 }))
        }
    }, [startHour, endHour])

    useEffect(() => {
        updateInterventionEvent(selectedEvent)
    }, [selectedEvent])

    return (
        <>
            <div onMouseDown={e => e.stopPropagation()}>
                <ModalSectionHeader label={i18n('Attributes')} />
                <ModalField
                    label={`Name`}
                    field={
                        <div className="input-normal">
                            <input
                                disabled={!hasPermissionToEdit || fieldDisabledAfterEntityStart}
                                style={errors?.name ? { borderColor: '#D9534F' } : {}}
                                type="text"
                                value={selectedEvent.name || ''}
                                onChange={e => {
                                    e.persist()
                                    setSelectedEvent(prevstate => ({ ...prevstate, name: e?.target?.value }))
                                }}
                                placeholder={i18n('Intervention name')}
                                id="intervention-name"
                            />
                        </div>
                    }
                />
                <ModalField
                    label={'Intervention Tatic'}
                    field={
                        <>
                            <Select
                                disabled={!hasPermissionToEdit}
                                style={errors?.tatic ? { borderColor: '#D9534F' } : {}}
                                searchable={false}
                                clearable={false}
                                options={taticList}
                                placeholder={
                                    selectedEvent.tatic
                                        ? `${taticList.find(tatic => tatic.value === selectedEvent.tatic)}`
                                        : i18n('Select...')
                                }
                                value={selectedEvent.tatic !== '' && selectedEvent.tatic}
                                onChange={value => {
                                    setSelectedEvent(prevstate => ({
                                        ...prevstate,
                                        tatic: value
                                    }))
                                }}
                                id="intervention-tatic"
                            />
                        </>
                    }
                />
                <ModalField
                    label={'Responsible'}
                    field={
                        <div className="input-normal">
                            <input
                                disabled={!hasPermissionToEdit}
                                style={errors?.responsible ? { borderColor: '#D9534F' } : {}}
                                type="text"
                                value={selectedEvent.responsible || ''}
                                onChange={e => {
                                    e.persist()
                                    setSelectedEvent(prevstate => ({ ...prevstate, responsible: e?.target?.value }))
                                }}
                                placeholder={i18n('Accountable name')}
                                id="intervention-responsible"
                            />
                        </div>
                    }
                />
                <ModalField
                    field={
                        <div className="input-date-time">
                            <ModalField
                                label={'Start Date'}
                                field={
                                    <CustomDatePicker
                                        isDisabled={!hasPermissionToEdit || fieldDisabledAfterEntityStart}
                                        date={startDate}
                                        setDate={setStartDate}
                                    />
                                }
                            />
                            <ModalField
                                label={'Start Hour'}
                                field={
                                    <MaskedInput
                                        disabled={!hasPermissionToEdit || fieldDisabledAfterEntityStart}
                                        style={errors?.start ? { borderColor: '#D9534F' } : {}}
                                        mask="11:11"
                                        type="text"
                                        placeholder={'00:00'}
                                        value={startHour}
                                        onChange={e => setStartHour(e?.target?.value)}
                                        id="intervention-startHour"
                                    />
                                }
                            />
                        </div>
                    }
                />
                {selectedEvent.state != STATIC_DATA_STATES.EMPTY && (
                    <ModalField
                        field={
                            <div className="input-date-time">
                                <ModalField
                                    label={'End Date'}
                                    field={
                                        <CustomDatePicker
                                            isDisabled={
                                                !hasPermissionToEdit ||
                                                selectedEvent.state === STATIC_DATA_STATES.FINISHED
                                            }
                                            style={
                                                errors?.end && eventEndDateTime === null
                                                    ? { borderColor: '#D9534F' }
                                                    : undefined
                                            }
                                            date={endDate}
                                            setDate={setEndDate}
                                        />
                                    }
                                />
                                <ModalField
                                    label={'End Hour'}
                                    field={
                                        <MaskedInput
                                            disabled={
                                                !hasPermissionToEdit ||
                                                selectedEvent.state === STATIC_DATA_STATES.FINISHED
                                            }
                                            style={errors?.end ? { borderColor: '#D9534F' } : {}}
                                            mask="11:11"
                                            type="text"
                                            placeholder={'00:00'}
                                            value={endHour}
                                            onChange={e => setEndHour(e?.target?.value)}
                                            id="intervention-endHour"
                                        />
                                    }
                                />
                            </div>
                        }
                    />
                )}
            </div>
        </>
    )
}

export default InterventionTab
